import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import useSittingStore from '../../../stores/sittingStore';
import useVisitStore from '../../../stores/visitStore';
import useSittingActions from '../../../hooks/useSittingActions';
import { formatToken } from '../../../utils/formatToken';
import { isRecentlyCompleted } from '../../../utils/completedWindow';
import RoleTaskBadge from './RoleTaskBadge';
import ProcedureChipList from './ProcedureChipList';
import { parseReceptionistQuery } from './parseReceptionistQuery';

/**
 * DoctorProceduresWidget — Phase 4.0e
 *
 * Single teal pill on the doctor's right edge that consolidates the
 * two cross-visit axes from 4.0d:
 *   - "My tasks"   — sittings the doctor must act on (assignee axis)
 *   - "Delegated"  — sittings the doctor delegated to someone else
 *                    (delegator axis, sticky until terminal)
 *
 * Hover-opens to a panel with two tabs at the top, each rendering its
 * own filtered chip list. Replaces NurseTaskWidget + DelegatedByMeWidget
 * for the DOCTOR role specifically — nurses keep the single-pill widget
 * since they have no delegator axis.
 *
 * The per-visit disc inside DoctorQueueView (top:50%) is unchanged and
 * complements this widget — that's the live consultation context, this
 * is the cross-visit "what do I owe people / what do people owe me".
 */

const TERMINAL_STATUSES = new Set(['completed', 'paid', 'cancelled']);

/**
 * Order the per-visit task groups: today's visits before past-day visits,
 * and within each bucket newest visit first (latest-to-old). Recency is the
 * group's most-recent sitting `created_at` (`sortTs`), with a higher
 * `queueNumber` (later registration today) as the tiebreak when timestamps
 * are equal or missing. Pure + exported for unit testing.
 */
export function compareDoctorTaskGroups(a, b) {
  if (a.past !== b.past) return a.past ? 1 : -1;       // today before past
  if ((b.sortTs || 0) !== (a.sortTs || 0)) {
    return (b.sortTs || 0) - (a.sortTs || 0);          // newest visit first
  }
  return (b.queueNumber ?? 0) - (a.queueNumber ?? 0);  // later queue # first
}

// Chips whose owning visit's queue day is strictly before today are
// considered "past" — surfaced under the dedicated Past pill so today's
// per-visit Q-X pills don't collide with yesterday's queue numbers (queue
// numbers reset daily). The day anchor lives in `isPastQueueDay` below,
// inside the component, since it needs the loaded visit rows.
//
// `ymdLocal` returns the LOCAL calendar date (clinic timezone). Both
// "today" and each chip's date must be derived the same way: the backend
// stamps created_at in IST (+05:30), so comparing those against a UTC
// "today" (the old `toISOString().slice(0,10)`) wrongly read yesterday's
// chips as today's between IST midnight and 05:30, when UTC still points at
// the previous day — that was the duplicate-Q-1 bug.
function ymdLocal(input) {
  const d = input instanceof Date ? input : new Date(input);
  if (Number.isNaN(d.getTime())) return null;
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}
function todayYmd() {
  return ymdLocal(new Date());
}

// Assignee axis ('all' | 'mine' | 'delegated') persisted per member, plus a
// single show-completed flag. Replaces the old My/Delegated tab persistence.
const ASSIGNEE_KEY = (memberId) => `doctor-procs:assignee:${memberId || 'anon'}`;
const SHOW_COMPLETED_KEY = (memberId) =>
  `doctor-procs:show-completed:${memberId || 'anon'}`;

function readSession(key, fallback) {
  if (typeof window === 'undefined') return fallback;
  try { return window.sessionStorage?.getItem(key) ?? fallback; }
  catch (_e) { return fallback; }
}

function writeSession(key, value) {
  if (typeof window === 'undefined') return;
  try { window.sessionStorage?.setItem(key, value); }
  catch (_e) { /* ignore */ }
}

export default function DoctorProceduresWidget({ viewer, verticalAnchorPx }) {
  const myQueueRaw = useSittingStore((s) => s.myQueue);
  const delegatedByMeRaw = useSittingStore((s) => s.delegatedByMe);
  const fetchMyQueue = useSittingStore((s) => s.fetchMyQueue);
  const fetchDelegatedByMe = useSittingStore((s) => s.fetchDelegatedByMe);
  // Without `myMemberId` set, the store's `patchAcrossProjections` skips
  // the WS-driven delegatedByMe upsert (its `else if (fullSummary && myId)`
  // branch). Previously only ProcedureQueuePage seeded this, so when the
  // widget mounted on the dashboard (SmartWorkflowWidget) a freshly
  // created delegation never landed until a manual refetch. Seed it from
  // viewer.memberId here so every mount point keeps the upsert alive.
  const setMyMemberId = useSittingStore((s) => s.setMyMemberId);
  // Subscribe to BOTH slices: `dailyVisits` is populated by the daily
  // dashboard fetch (this is what `SmartWorkflowWidget` uses to drive
  // the screen), while `visits` is populated by the filtered visits
  // endpoint. We merge both — deduped by visit_id below in
  // activeQueues — so the queue-filter strip works whichever pipeline
  // populated the store first. Subscribing to only one would silently
  // hide the strip on routes that prefill the other.
  const dailyVisits = useVisitStore((s) => s.dailyVisits);
  const visits = useVisitStore((s) => s.visits);

  // Initial fetch — both axes hydrate in parallel. WS handlers in the
  // store keep them fresh after that.
  //
  // Phase 13: previously these two calls fired sequentially in the
  // same synchronous block — JS scheduled them as two separate macro
  // tasks rather than overlapping in flight. Now they're explicitly
  // launched together and surfaced via `Promise.allSettled` so a slow
  // lens doesn't gate the other. Cuts widget-open latency from
  // (t1 + t2) to max(t1, t2) on typical busy days.
  useEffect(() => {
    if (viewer?.memberId && typeof setMyMemberId === 'function') {
      setMyMemberId(viewer.memberId);
    }
  }, [viewer?.memberId, setMyMemberId]);

  useEffect(() => {
    if (process.env.NODE_ENV === 'development') {
      // eslint-disable-next-line no-console
      console.time('procedures-widget-open');
    }
    const tasks = [];
    if (typeof fetchMyQueue === 'function') tasks.push(fetchMyQueue());
    if (typeof fetchDelegatedByMe === 'function') tasks.push(fetchDelegatedByMe());
    Promise.allSettled(tasks).then(() => {
      if (process.env.NODE_ENV === 'development') {
        // eslint-disable-next-line no-console
        console.timeEnd('procedures-widget-open');
      }
    });
  }, [fetchMyQueue, fetchDelegatedByMe]);

  // Doctor's Pending Tasks dock never shows awaiting_payment chips:
  // when a visit is non-terminal, those chips belong to the per-visit
  // half-disc widget; when a visit is COMPLETED, they belong to the
  // receptionist's Action-Pending list. Strip them at the source.
  const myQueue = useMemo(
    () => (myQueueRaw || []).filter((s) => s?.status !== 'awaiting_payment'),
    [myQueueRaw],
  );
  const delegatedByMe = useMemo(
    () => (delegatedByMeRaw || []).filter((s) => s?.status !== 'awaiting_payment'),
    [delegatedByMeRaw],
  );

  // Predicates used by both the activeQueues memo and the My/Delegated
  // sort memos below. Declared up-front so activeQueues can reference
  // them without hitting a temporal-dead-zone error.
  const myMemberId = viewer?.memberId || null;
  const isDelegatedAway = (s) => {
    if (!myMemberId) return false;
    const assignedTo = s?.assigned_to?.member_id || s?.assigned_to_member_id || null;
    const assignedBy = s?.assigned_by?.member_id || s?.assigned_by_member_id || null;
    return assignedBy === myMemberId && assignedTo && assignedTo !== myMemberId;
  };
  const isCurrentlyMine = (s) => {
    if (!myMemberId) return false;
    const assignedTo = s?.assigned_to?.member_id || s?.assigned_to_member_id || null;
    return assignedTo === myMemberId;
  };

  // visit_id → the day (YYYY-MM-DD) the owning visit was created. A sitting
  // carries the visit's queue_number denormalized but NOT the visit's date,
  // so the queue number's day has to come from the visit row. Shared by the
  // activeQueues memo (pill bucketing) and matchesQueueFilter (Past tab /
  // per-visit filter) so both agree on what counts as "past".
  const visitCreatedById = useMemo(() => {
    const m = new Map();
    const merged = [
      ...(Array.isArray(dailyVisits) ? dailyVisits : []),
      ...(Array.isArray(visits) ? visits : []),
    ];
    for (const v of merged) {
      const vid = v?.visit_id || v?.id;
      if (!vid || m.has(vid)) continue;
      const day = v?.created_at ? ymdLocal(v.created_at) : null;
      if (day) m.set(vid, day);
    }
    return m;
  }, [dailyVisits, visits]);

  // The doctor's ACTUAL active consultation = the single IN_PROGRESS visit
  // they're currently in. Mirrors DoctorQueueView's
  // `currentVisit = visibleInProgress[0]` and useResumeVisit's `inProgressFor`.
  // We must NOT infer "active" from a group's own visit_status field: stale or
  // never-closed PAST visits also read IN_PROGRESS, which made the old check
  // treat every visit as active and hide Resume on all of them. Only the visit
  // whose id matches this should suppress Resume.
  const currentVisitId = useMemo(() => {
    const merged = [
      ...(Array.isArray(dailyVisits) ? dailyVisits : []),
      ...(Array.isArray(visits) ? visits : []),
    ];
    const current = merged.find(
      (v) => v?.status === 'IN_PROGRESS'
        && (v.doctor_member_id === myMemberId || v.viewer_role === 'DOCTOR')
        && (!v.action_pending_with_member_id
          || v.action_pending_with_member_id === myMemberId),
    );
    return current?.visit_id || current?.id || null;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dailyVisits, visits, myMemberId]);

  // A sitting whose visit is the doctor's current live consultation is being
  // handled in the per-visit Queue disc, so it must not also show in Pending
  // Tasks. Reactive: currentVisitId tracks resume (→IN_PROGRESS, excluded) and
  // complete (→COMPLETED, currentVisitId moves on, sitting reappears) live.
  const isInLiveQueue = (s) => !!currentVisitId && s?.visit_id === currentVisitId;

  // The day a queue number belongs to is the VISIT's creation day (queue
  // numbers reset daily). Fall back to the sitting's own created_at when the
  // visit isn't loaded; scheduled_for is intentionally NOT used here — a
  // prior-day visit's procedure scheduled for today must still bucket as past.
  const queueDayYmd = (s) =>
    visitCreatedById.get(s?.visit_id)
    ?? (s?.created_at ? ymdLocal(s.created_at) : null);
  const isPastQueueDay = (s, today = todayYmd()) => {
    const d = queueDayYmd(s);
    return d != null && d < today;
  };

  // Active visits — one queue-filter pill per IN_PROGRESS visit on the
  // doctor's screen. Each entry { queueNumber, token } is the data the
  // pill row needs: queueNumber is the filter value applied to chips,
  // token is the user-facing label (`Q-5`, `Q-5#1` for appointments).
  // Deduped by queueNumber defensively in case the visits list ever
  // contains duplicates. When no IN_PROGRESS visits exist, this is an
  // empty array and the Scope control collapses to All-queues-only
  // (the whole line is hidden when there are no queues and no past work).
  const { activeQueues, hasPastPending } = useMemo(() => {
    // Pills are keyed by visit_id (unique forever), NOT queue_number.
    // Queue numbers reset daily from 1, so filtering by queue_number
    // would wrongly match yesterday's Q-1 chips when the user picks
    // today's Q-1 — that was the cross-day bug. The user-facing label
    // is still the `Q-X` token, but the underlying identity is the
    // visit_id so filtering only ever matches one specific visit.
    //
    // Per-visit Q-X pills only surface for TODAY's chips. Yesterday's
    // pending work is folded into a single "Past" pill so the row
    // doesn't end up with two pills both labeled "Q-3" (one today,
    // one yesterday). The Past pill appears whenever any non-terminal
    // chip's date is strictly before today.
    const visitsMerged = [
      ...(Array.isArray(dailyVisits) ? dailyVisits : []),
      ...(Array.isArray(visits) ? visits : []),
    ];
    const visitInfoById = new Map();
    for (const v of visitsMerged) {
      const vid = v?.visit_id || v?.id;
      if (!vid || visitInfoById.has(vid)) continue;
      visitInfoById.set(vid, {
        queueNumber: v?.queue_number ?? null,
        appointmentNumber: v?.appointment_number ?? null,
      });
    }

    const today = todayYmd();
    const seenVisit = new Set();
    const out = [];
    let pastSeen = false;
    const considerChip = (s) => {
      // Chips whose owning visit's queue day is before today fold into the
      // Past pill instead of generating a per-visit Q-X pill of their own —
      // otherwise a prior-day visit's queue_number (which resets daily) would
      // re-surface as a duplicate today pill (e.g. two "Q-1"s).
      if (isPastQueueDay(s, today)) {
        pastSeen = true;
        return;
      }
      const vid = s?.visit_id;
      // Sittings without a visit_id can't be safely scoped to today's
      // queue (e.g. future-scheduled, not yet bound). Skip them — they
      // remain visible under the Overall view either way.
      if (!vid || seenVisit.has(vid)) return;
      const meta = visitInfoById.get(vid) || {};
      const qn = s?.queue_number ?? meta.queueNumber;
      const appt = s?.appointment_number ?? meta.appointmentNumber ?? null;
      if (qn == null) return;
      const raw = formatToken(qn, appt, { hash: false });
      if (!raw) return;
      seenVisit.add(vid);
      out.push({ visitId: vid, queueNumber: qn, token: `Q-${raw}`, patientName: s?.patient_name || '' });
    };

    // My Tasks axis — only chips currently assigned to me, non-
    // terminal, not delegated away. Mirrors the predicate used by
    // mineSorted.active so the pill set agrees with what the user
    // sees inside the My Tasks tab.
    for (const s of myQueue || []) {
      if (TERMINAL_STATUSES.has(s?.status)) continue;
      if (!isCurrentlyMine(s)) continue;
      if (isDelegatedAway(s)) continue;
      if (isInLiveQueue(s)) continue;
      considerChip(s);
    }

    // Delegated axis — every non-terminal chip the doctor delegated
    // out, since those still represent pending work for the queue.
    for (const s of delegatedByMe || []) {
      if (TERMINAL_STATUSES.has(s?.status)) continue;
      if (isInLiveQueue(s)) continue;
      considerChip(s);
    }

    out.sort((a, b) => a.queueNumber - b.queueNumber);
    return { activeQueues: out, hasPastPending: pastSeen };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dailyVisits, visits, myQueue, delegatedByMe, myMemberId, currentVisitId]);
  const activeQueueKeySig = `${activeQueues.map((q) => q.visitId).join('|')}|past:${hasPastPending ? '1' : '0'}`;

  // Queue-filter state. Flavors:
  //   - 'today'      → only today's chips (the DEFAULT — past folded out)
  //   - null         → All queues (everything pending, today + past)
  //   - 'past'       → only chips dated before today
  //   - <visit_id>   → only chips on that specific visit
  // Filtering by visit_id (not queue_number) because queue_numbers
  // reset daily — a queue_number key would collide across days.
  const [queueFilter, setQueueFilter] = useState('today');
  // When the set of pills changes (a visit closed, past chips
  // resolved, etc.), reset any stale queue filter so the user isn't
  // stuck on a pill that no longer exists.
  useEffect(() => {
    // 'today' and null (All queues) are always valid scopes.
    if (queueFilter == null || queueFilter === 'today') return;
    if (queueFilter === 'past') {
      if (!hasPastPending) setQueueFilter('today');
      return;
    }
    if (!activeQueues.some((q) => q.visitId === queueFilter)) {
      setQueueFilter('today');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeQueueKeySig]);

  const reload = () => {
    if (typeof fetchMyQueue === 'function') fetchMyQueue().catch(() => {});
    if (typeof fetchDelegatedByMe === 'function') fetchDelegatedByMe().catch(() => {});
  };

  const { handleAction: rawHandleAction, dialogs } = useSittingActions({
    viewer,
    onAfterAction: reload,
  });

  // When a queue filter is active the user has already disambiguated
  // the patient by clicking the Q-X pill, so the start-confirmation
  // dialog ("is this the right patient?") becomes redundant. Wrap
  // handleAction to inject skipConfirm at action time.
  const handleAction = useCallback(
    (event) => rawHandleAction({ ...event, skipConfirm: queueFilter != null }),
    [rawHandleAction, queueFilter],
  );

  // ── Derived rows + counts ──────────────────────────────────────
  // Backend's default `/sittings/assigned` lens merges assignee + visit-
  // doctor axes (Phase 1d). For a doctor that means anything they
  // delegated still rides axis 2 (they're still the visit doctor) and
  // pollutes "My tasks" with rows that already live in the Delegated
  // tab. The `isDelegatedAway` and `isCurrentlyMine` predicates needed
  // to filter that out are declared above the activeQueues memo so
  // both code paths share the same definition.

  // Q-X / Past filter: narrow both axes to:
  //   - everything (queueFilter == null),
  //   - past chips (queueFilter === 'past'),
  //   - chips on a specific visit (queueFilter === <visit_id>).
  // Filtering by visit_id rather than queue_number because queue
  // numbers reset daily and would let yesterday's Q-1 leak into
  // today's Q-1 view. Past chips are intentionally excluded from
  // visit-id filters so today's Q-X view never shows past data.
  const matchesQueueFilter = (s) => {
    if (queueFilter == null) return true;
    if (queueFilter === 'today') return !isPastQueueDay(s);
    if (queueFilter === 'past') return isPastQueueDay(s);
    if (isPastQueueDay(s)) return false;
    return s?.visit_id === queueFilter;
  };

  // `isCurrentlyMine` is declared above (alongside isDelegatedAway and
  // myMemberId) so the activeQueues memo can use the same predicate
  // without a temporal-dead-zone error.

  // Assignee lens lists, before the queue/search filters. `mine` is the
  // doctor's own actionable work; `delegated` is what they handed off.
  // The two are disjoint (a chip delegated away is no longer "mine"), so
  // the All view is a plain concat.
  const mineList = useMemo(
    () => (myQueue || []).filter((s) => isCurrentlyMine(s) && !isDelegatedAway(s)),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [myQueue, myMemberId],
  );
  const delegatedList = useMemo(() => delegatedByMe || [], [delegatedByMe]);

  // Segmented-control counts: non-terminal work per axis (unfiltered by
  // queue/search so the labels stay a stable "how much is on this axis").
  // All three counts follow the active scope (Today / Q-X / Past / All) so the
  // tab labels always equal what's shown and stay arithmetically consistent
  // (All = Mine + Delegated). The assignee tabs and the scope filter are fully
  // independent — switching a tab never changes the scope. Earlier-day work is
  // surfaced by the past-count hint in the body, not by mutating either filter.
  const mineCount = useMemo(
    () => mineList.filter((s) => !TERMINAL_STATUSES.has(s.status) && !isInLiveQueue(s) && matchesQueueFilter(s)).length,
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [mineList, currentVisitId, queueFilter],
  );
  const delegatedCount = useMemo(
    () => delegatedList.filter((s) => !TERMINAL_STATUSES.has(s.status) && !isInLiveQueue(s) && matchesQueueFilter(s)).length,
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [delegatedList, currentVisitId, queueFilter],
  );
  const allCount = mineCount + delegatedCount;

  // ── Assignee filter ('all' default), search box, show-completed ──
  const persistedAssignee = readSession(ASSIGNEE_KEY(viewer?.memberId), null);
  const [assignee, setAssigneeRaw] = useState(persistedAssignee || 'all');
  const setAssignee = (next) => {
    setAssigneeRaw(next);
    writeSession(ASSIGNEE_KEY(viewer?.memberId), next);
    // Assignee and scope are independent — switching tabs must not silently
    // move the scope filter (that was the confusing coupling we removed).
    // eslint-disable-next-line no-console
    console.info(`metric=procedures_assignee_switched axis=${next}`);
  };

  const [query, setQuery] = useState('');
  const parsedQuery = useMemo(() => parseReceptionistQuery(query), [query]);
  // Doctor sittings carry patient_name + queue_number but no mobile, so a
  // mobile/ambiguous fragment falls back to a patient-name contains.
  const matchesSearch = useCallback((s) => {
    switch (parsedQuery.intent) {
      case 'empty':
        return true;
      case 'queue':
        return s?.queue_number === parsedQuery.value;
      case 'queueOrMobile':
        return s?.queue_number === parsedQuery.value
          || (s?.patient_name || '').toLowerCase().includes(parsedQuery.fragment);
      case 'mobile':
        return (s?.patient_name || '').toLowerCase().includes(parsedQuery.fragment);
      case 'name':
        return (s?.patient_name || '').toLowerCase().includes(parsedQuery.text);
      default:
        return true;
    }
  }, [parsedQuery]);

  const [showCompleted, setShowCompletedRaw] = useState(
    () => readSession(SHOW_COMPLETED_KEY(viewer?.memberId), '0') === '1',
  );
  const setShowCompleted = (next) => {
    setShowCompletedRaw(next);
    writeSession(SHOW_COMPLETED_KEY(viewer?.memberId), next ? '1' : '0');
    // eslint-disable-next-line no-console
    console.info(`metric=show_completed_toggled value=${next ? 'on' : 'off'}`);
  };

  // When a specific scope is chosen (a Q-X visit or Past) the user is asking
  // for the FULL picture of that scope (done + pending), so completed chips are
  // folded in automatically without the show-completed toggle. 'today' and null
  // (All queues) are broad defaults, not a narrowing — they keep the toggle and
  // hide completed by default.
  const queueFilterActive = queueFilter != null && queueFilter !== 'today';

  // ── Grouped, filtered body ──────────────────────────────────────
  // Take the chosen assignee lens, apply queue + search filters, then
  // group by visit_id into per-patient cards. Each group summarises the
  // patient's pending procedures and offers a Resume-visit jump.
  const axisSittings = useMemo(() => {
    if (assignee === 'mine') return mineList;
    if (assignee === 'delegated') return delegatedList;
    return [...mineList, ...delegatedList];
  }, [assignee, mineList, delegatedList]);

  const { groups, matchCount, consideredCount } = useMemo(() => {
    const byVisit = new Map();
    let considered = 0;
    let matched = 0;
    for (const s of axisSittings) {
      if (isInLiveQueue(s)) continue;
      const terminal = TERMINAL_STATUSES.has(s?.status);
      if (terminal && !(showCompleted || queueFilterActive)) continue;
      // 3-day cap: completed/terminal items drop off the live board once they're
      // older than the window (full history stays on the patient's visit page).
      // Incomplete items are never capped.
      if (terminal && !isRecentlyCompleted(s)) continue;
      if (!matchesQueueFilter(s)) continue;
      considered += 1;
      if (!matchesSearch(s)) continue;
      matched += 1;
      const vid = s?.visit_id || `__novisit__:${s?.sitting_id}`;
      if (!byVisit.has(vid)) {
        byVisit.set(vid, {
          key: vid,
          visitId: s?.visit_id || null,
          patientName: s?.patient_name || 'Unknown patient',
          queueNumber: s?.queue_number ?? null,
          appointmentNumber: s?.appointment_number ?? null,
          visitStatus: s?.visit_status || null,
          past: isPastQueueDay(s),
          sittings: [],
          total: 0,
          pendingCount: 0,
          sortTs: 0,   // newest sitting's created_at — drives latest-first order
        });
      }
      const g = byVisit.get(vid);
      g.sittings.push(s);
      g.total += Number(s?.final_cost) || 0;
      if (!terminal) g.pendingCount += 1;
      const ts = Date.parse(s?.created_at || '');
      if (!Number.isNaN(ts) && ts > g.sortTs) g.sortTs = ts;
    }
    const out = [...byVisit.values()];
    for (const g of out) {
      const raw = g.queueNumber != null
        ? formatToken(g.queueNumber, g.appointmentNumber, { hash: false })
        : null;
      g.queueToken = raw ? `Q-${raw}` : null;
    }
    // Today's groups first, then past; within a bucket newest visit first.
    out.sort(compareDoctorTaskGroups);
    return { groups: out, matchCount: matched, consideredCount: considered };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [axisSittings, showCompleted, queueFilter, parsedQuery, currentVisitId]);

  // The closed badge count is UNFILTERED — "X pending tasks across all
  // patients" — so the doctor knows the global load even when a queue
  // filter is active inside the panel. Inner tab counters (mineCount /
  // delegatedCount) follow the filter.
  //
  // Important: this count uses the broader "any non-terminal procedure"
  // rule, not the stricter `isActionable` rule that the inner My-Tasks
  // tab uses. Otherwise statuses like `pending_approval`, `held`, or
  // `needs_reassignment` would not register on the badge and the badge
  // would disappear even when the doctor has work pending — which would
  // hide the only entry point to those chips. Inside the panel, the
  // existing per-tab rendering still applies its own actionable rule.
  const unfilteredTotalCount = useMemo(() => {
    let n = 0;
    for (const s of myQueue || []) {
      if (!isCurrentlyMine(s)) continue;
      if (isDelegatedAway(s)) continue;
      if (TERMINAL_STATUSES.has(s.status)) continue;
      if (isInLiveQueue(s)) continue;
      n += 1;
    }
    for (const s of delegatedByMe || []) {
      if (TERMINAL_STATUSES.has(s.status)) continue;
      if (isInLiveQueue(s)) continue;
      n += 1;
    }
    return n;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [myQueue, delegatedByMe, myMemberId, currentVisitId]);
  const totalCount = unfilteredTotalCount;

  // Earlier-day pending work, counted the same way as the badge (non-terminal,
  // not in the live queue, both axes) but only for past queue days. Surfaced in
  // the panel under the Today default so the global badge number reconciles:
  // `today shown + pastPendingCount + live-visit (hidden) = badge`.
  const pastPendingCount = useMemo(() => {
    let n = 0;
    for (const s of myQueue || []) {
      if (!isCurrentlyMine(s) || isDelegatedAway(s)) continue;
      if (TERMINAL_STATUSES.has(s.status) || isInLiveQueue(s)) continue;
      if (isPastQueueDay(s)) n += 1;
    }
    for (const s of delegatedByMe || []) {
      if (TERMINAL_STATUSES.has(s.status) || isInLiveQueue(s)) continue;
      if (isPastQueueDay(s)) n += 1;
    }
    return n;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [myQueue, delegatedByMe, myMemberId, currentVisitId]);

  // ── Render helpers ─────────────────────────────────────────────
  // `palette` is supplied by RoleTaskBadge via the children render-prop
  // (see renderBody below). Using its solid tokens — `bgAlt`, `border`,
  // `text`, `muted` — keeps day-mode contrast consistent with the chip
  // area and dodges the rgba-on-white fade-out covered by the
  // CLAUDE.md transparency rule.

  // Queue/time filter — a single slim "Scope" line whose dropdown holds
  // Overall / Q-X (one per visit with pending chips today) / Past. Replaces
  // the old wrapping pill strip (which grew unbounded with patient count)
  // with one compact control. When neither today's queues nor past chips
  // exist the line collapses to null entirely. State + semantics are
  // unchanged — only the surface differs.
  const renderScopeFilter = (palette) => {
    if (activeQueues.length === 0 && !hasPastPending) return null;
    return (
      <ScopeFilterControl
        palette={palette}
        queueFilter={queueFilter}
        setQueueFilter={setQueueFilter}
        activeQueues={activeQueues}
        hasPastPending={hasPastPending}
      />
    );
  };

  const renderSearch = () => (
    <div style={{ position: 'relative', marginBottom: 8 }}>
      <span
        aria-hidden="true"
        className="text-slate-500 dark:text-slate-300"
        style={{
          position: 'absolute',
          left: 11,
          top: '50%',
          transform: 'translateY(-50%)',
          fontSize: '0.9rem',
          pointerEvents: 'none',
          lineHeight: 1,
        }}
      >
        🔍
      </span>
      <input
        type="text"
        value={query}
        onChange={(e) => setQuery(e.target.value)}
        placeholder="Search patient or Q-number…"
        aria-label="Search pending tasks"
        className="bg-slate-100 text-slate-900 placeholder-slate-500 border-slate-400 focus:border-teal-500 focus:ring-2 focus:ring-teal-500/40 dark:bg-slate-700 dark:text-slate-50 dark:placeholder-slate-300 dark:border-slate-500"
        style={{
          width: '100%',
          boxSizing: 'border-box',
          padding: '0.5rem 2rem 0.5rem 2.1rem',
          borderRadius: 10,
          borderWidth: 1,
          borderStyle: 'solid',
          fontSize: '0.8125rem',
          fontWeight: 500,
          outline: 'none',
        }}
      />
      {query.trim() && (
        <button
          type="button"
          onClick={() => setQuery('')}
          aria-label="Clear search"
          className="bg-slate-200 text-slate-700 hover:bg-slate-300 dark:bg-slate-600 dark:text-slate-100 dark:hover:bg-slate-500"
          style={{
            position: 'absolute',
            right: 7,
            top: '50%',
            transform: 'translateY(-50%)',
            border: 'none',
            cursor: 'pointer',
            fontSize: '0.7rem',
            lineHeight: 1,
            padding: '0.15rem 0.3rem',
            borderRadius: 6,
          }}
        >
          ✕
        </button>
      )}
    </div>
  );

  const ASSIGNEE_TABS = [
    { key: 'mine', label: `Mine (${mineCount})`, activeCls: 'bg-teal-600 text-white hover:bg-teal-700' },
    { key: 'delegated', label: `Delegated (${delegatedCount})`, activeCls: 'bg-blue-600 text-white hover:bg-blue-700' },
    { key: 'all', label: `All (${allCount})`, activeCls: 'bg-teal-600 text-white hover:bg-teal-700' },
  ];

  const renderAssigneeFilter = (palette) => (
    <div
      role="tablist"
      aria-label="Filter by assignee"
      style={{
        display: 'flex',
        gap: 4,
        padding: 3,
        borderRadius: 8,
        background: palette.bgAlt,
        border: `1px solid ${palette.border}`,
        marginBottom: 6,
      }}
    >
      {ASSIGNEE_TABS.map(({ key, label, activeCls }) => {
        const active = assignee === key;
        return (
          <button
            key={key}
            type="button"
            role="tab"
            aria-selected={active}
            onClick={() => setAssignee(key)}
            className={active
              ? activeCls
              : 'bg-white text-slate-800 hover:bg-slate-100 dark:bg-slate-800 dark:text-slate-100 dark:hover:bg-slate-700'}
            style={{
              flex: 1,
              padding: '0.4rem 0.5rem',
              borderRadius: 6,
              border: active ? 'none' : `1px solid ${palette.border}`,
              fontSize: '0.75rem',
              fontWeight: 600,
              cursor: 'pointer',
            }}
          >
            {label}
          </button>
        );
      })}
    </div>
  );

  // Body: pending procedures grouped per patient/visit. Each group is a
  // card with a summary header (patient · Q-token · count · ₹total) and a
  // Resume-visit jump; the chips themselves reuse ProcedureChipList (which
  // applies its own Running/Queue/Done sectioning).
  const renderGroups = (palette) => {
    const searchActive = parsedQuery.intent !== 'empty';
    // Under the Today default, earlier-day pending work is hidden — surface its
    // count with quick switches so the global PENDING badge reconciles. Shown
    // both when today is empty (as the empty state) and as a footer otherwise.
    const showPastHint = queueFilter === 'today' && pastPendingCount > 0 && !searchActive;
    const switchBtnCls =
      'rounded-md border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-800 '
      + 'text-teal-700 dark:text-teal-300 hover:bg-slate-100 dark:hover:bg-slate-700';
    const switchBtnStyle = {
      padding: '0.15rem 0.45rem',
      fontSize: '0.6875rem',
      fontWeight: 700,
      cursor: 'pointer',
    };
    const pastSwitches = (
      <span style={{ display: 'inline-flex', gap: 6 }}>
        <button type="button" className={switchBtnCls} style={switchBtnStyle} onClick={() => setQueueFilter(null)}>
          All queues
        </button>
        <button type="button" className={switchBtnCls} style={switchBtnStyle} onClick={() => setQueueFilter('past')}>
          Show past
        </button>
      </span>
    );
    if (groups.length === 0) {
      if (showPastHint) {
        return (
          <div
            style={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'flex-start',
              gap: 8,
              fontSize: '0.875rem',
              fontWeight: 600,
              color: palette.text,
            }}
          >
            <span>
              No tasks for today. <strong>{pastPendingCount}</strong> earlier task
              {pastPendingCount === 1 ? '' : 's'} pending.
            </span>
            {pastSwitches}
          </div>
        );
      }
      return (
        <p style={{ margin: 0, fontSize: '0.75rem', opacity: 0.7 }}>
          {searchActive
            ? `No matches${query.trim() ? ` for “${query.trim()}”` : ''}. Clear to see all.`
            : '✅ All clear! No pending tasks.'}
        </p>
      );
    }
    return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
        {searchActive && (
          <p style={{ margin: 0, fontSize: '0.6875rem', color: palette.muted }}>
            {matchCount} of {consideredCount} match
          </p>
        )}
        {groups.map((g) => {
          // Resume re-opens any visit with pending work so the doctor can jump
          // back in. Hidden ONLY for the visit they're currently consulting
          // (the real current visit — NOT inferred from a per-visit status
          // field, since stuck/past visits also read IN_PROGRESS).
          const isActiveConsultation = !!currentVisitId && g.visitId === currentVisitId;
          const hasPending = !!g.visitId && g.pendingCount > 0;
          // Resume is a same-day affordance only: the doctor's workflow is
          // fed from today's visits, so a past-day visit can't be brought
          // forward. Show it for any same-day visit with pending work except
          // the one currently being consulted.
          const canResume = hasPending && !isActiveConsultation && !g.past;
          return (
          <div
            key={g.key}
            className="bg-white dark:bg-slate-800"
            style={{ borderRadius: 8, border: `1px solid ${palette.border}`, padding: 8 }}
          >
            <div
              style={{
                display: 'flex',
                alignItems: 'flex-start',
                justifyContent: 'space-between',
                gap: 8,
                marginBottom: 6,
              }}
            >
              <div style={{ minWidth: 0 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
                  <span
                    className="text-slate-800 dark:text-slate-100"
                    style={{ fontSize: '0.8125rem', fontWeight: 600 }}
                  >
                    {g.patientName}
                  </span>
                  {g.queueToken && (
                    <span
                      className="bg-teal-100 text-teal-800 dark:bg-teal-900/50 dark:text-teal-200"
                      style={{ fontSize: '0.625rem', fontWeight: 700, padding: '0.05rem 0.35rem', borderRadius: 999 }}
                    >
                      {g.queueToken}
                    </span>
                  )}
                  {g.past && (
                    <span
                      className="bg-slate-100 text-slate-600 dark:bg-slate-700 dark:text-slate-300"
                      style={{ fontSize: '0.625rem', fontWeight: 600, padding: '0.05rem 0.35rem', borderRadius: 999 }}
                    >
                      Past
                    </span>
                  )}
                </div>
                <div
                  className="text-slate-500 dark:text-slate-400"
                  style={{ fontSize: '0.6875rem', marginTop: 2 }}
                >
                  {g.pendingCount} pending
                  {g.total > 0 ? ` · ₹${g.total.toLocaleString('en-IN')}` : ''}
                </div>
              </div>
              {canResume && (
                <button
                  type="button"
                  onClick={() => handleAction({ action: 'resume_visit', sitting: g.sittings[0] })}
                  className="bg-emerald-600 hover:bg-emerald-700 text-white"
                  style={{
                    flexShrink: 0,
                    padding: '0.3rem 0.6rem',
                    borderRadius: 6,
                    border: 'none',
                    fontSize: '0.6875rem',
                    fontWeight: 600,
                    cursor: 'pointer',
                  }}
                >
                  ↻ Resume visit
                </button>
              )}
            </div>
            <ProcedureChipList
              sittings={g.sittings}
              viewer={viewer}
              onAction={handleAction}
              onDelete={null}
              compact={false}
              emptyHint={null}
              showPatient={false}
            />
          </div>
          );
        })}
        {/* Earlier-day pending not shown under the Today scope — reconciles
            the global PENDING badge with the today-only list. */}
        {showPastHint && (
          <div
            className="text-slate-500 dark:text-slate-400"
            style={{
              display: 'flex',
              flexWrap: 'wrap',
              alignItems: 'center',
              gap: 4,
              fontSize: '0.6875rem',
              paddingTop: 4,
              borderTop: `1px solid ${palette.border}`,
            }}
          >
            <span>+{pastPendingCount} earlier not shown —</span>
            {pastSwitches}
          </div>
        )}
        {/* Single show-completed toggle. Hidden under an active queue
            filter because that view force-includes completed chips. */}
        {!queueFilterActive && (
          <label
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 6,
              fontSize: '0.6875rem',
              paddingTop: 4,
              borderTop: `1px solid ${palette.border}`,
              cursor: 'pointer',
              userSelect: 'none',
            }}
          >
            <input
              type="checkbox"
              checked={showCompleted}
              onChange={(e) => setShowCompleted(e.target.checked)}
            />
            Show completed
          </label>
        )}
      </div>
    );
  };

  return (
    <>
      <RoleTaskBadge
        count={totalCount}
        countLabel="PENDING"
        title="Pending Tasks"
        tone="teal"
        verticalAnchorPx={verticalAnchorPx}
        ariaLabel={`${totalCount} pending task${totalCount === 1 ? '' : 's'}`}
        metricKey="procedures"
      >
        {({ palette }) => (
          <>
            {renderSearch()}
            {renderAssigneeFilter(palette)}
            {renderScopeFilter(palette)}
            {renderGroups(palette)}
          </>
        )}
      </RoleTaskBadge>
      {dialogs}
    </>
  );
}


const SCOPE_MENU_WIDTH = 220;

/**
 * ScopeFilterControl — the slim "Scope" line that replaces the old
 * queue-filter pill strip. A single dropdown holds Overall / one entry
 * per active Q-X visit / Past, so the panel never grows a wrapping pill
 * row as more patients queue. Selection drives the unchanged
 * `setQueueFilter`.
 *
 * Rendered INLINE (not portaled): the menu lives inside `wrapperRef`, so
 * the outside-click `contains` check covers both the trigger and the
 * items, and the item `onClick` fires reliably (a body-portaled menu was
 * racing the document mousedown-to-close and the click never landed).
 * The Scope line sits near the top of the panel, so a short downward menu
 * isn't clipped by the panel's overflow. Mirrors the proven
 * HeaderOverflowMenu pattern.
 */
function ScopeFilterControl({
  palette, queueFilter, setQueueFilter, activeQueues, hasPastPending,
}) {
  const [open, setOpen] = useState(false);
  const wrapperRef = useRef(null);
  const triggerRef = useRef(null);
  // The menu is positioned `fixed` (viewport-relative) rather than `absolute`,
  // so it escapes the panel body's `overflowY:auto` + the aside's
  // `overflow:hidden`. Otherwise, when today is empty the panel is short and the
  // downward menu gets clipped to just its first item. It still lives inside
  // wrapperRef in the DOM, so the outside-click `contains` check (and thus item
  // clicks) keep working without a portal. Position is recomputed from the
  // trigger's rect whenever it opens / the viewport changes.
  const [menuPos, setMenuPos] = useState(null);
  const recomputeMenuPos = useCallback(() => {
    const r = triggerRef.current?.getBoundingClientRect();
    if (!r) return;
    setMenuPos({ top: r.bottom + 6, right: Math.max(8, window.innerWidth - r.right) });
  }, []);

  const options = useMemo(() => {
    const opts = [
      { key: 'today', value: 'today', label: 'Today', sub: 'today only' },
      { key: 'overall', value: null, label: 'All queues', sub: 'today + past' },
    ];
    for (const q of activeQueues) {
      opts.push({ key: q.visitId, value: q.visitId, label: q.token, sub: q.patientName || '' });
    }
    if (hasPastPending) opts.push({ key: 'past', value: 'past', label: 'Past', sub: 'earlier days' });
    return opts;
  }, [activeQueues, hasPastPending]);

  // 'today' and null (All queues) are broad defaults — keep the trigger
  // un-narrowed (no teal fill); only a specific Q-X / Past reads as narrowed.
  const active = queueFilter != null && queueFilter !== 'today';
  const currentLabel = (() => {
    if (queueFilter === 'today') return 'Today';
    if (queueFilter == null) return 'All queues';
    if (queueFilter === 'past') return 'Past';
    const hit = activeQueues.find((q) => q.visitId === queueFilter);
    return hit ? hit.token : 'Today';
  })();

  useEffect(() => {
    if (!open) return undefined;
    const handlePointer = (e) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target)) setOpen(false);
    };
    const handleKey = (e) => { if (e.key === 'Escape') setOpen(false); };
    const handleReposition = () => recomputeMenuPos();
    document.addEventListener('mousedown', handlePointer);
    document.addEventListener('keydown', handleKey);
    window.addEventListener('resize', handleReposition);
    window.addEventListener('scroll', handleReposition, true);
    return () => {
      document.removeEventListener('mousedown', handlePointer);
      document.removeEventListener('keydown', handleKey);
      window.removeEventListener('resize', handleReposition);
      window.removeEventListener('scroll', handleReposition, true);
    };
  }, [open, recomputeMenuPos]);

  const isSelected = (opt) => {
    if (opt.value == null) return queueFilter == null;
    return queueFilter === opt.value;
  };

  const select = (value) => { setQueueFilter(value); setOpen(false); };

  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}>
      <span
        className="text-slate-500 dark:text-slate-400"
        style={{ fontSize: '0.6875rem', fontWeight: 700, letterSpacing: '0.04em', textTransform: 'uppercase' }}
      >
        Scope
      </span>
      <div ref={wrapperRef} style={{ position: 'relative', marginLeft: 'auto' }}>
        <button
          type="button"
          ref={triggerRef}
          aria-haspopup="menu"
          aria-expanded={open}
          aria-label={`Filter by queue — ${currentLabel}`}
          onClick={() => setOpen((v) => {
            const next = !v;
            if (next) recomputeMenuPos();
            return next;
          })}
          className={active
            ? 'bg-teal-600 text-white hover:bg-teal-700'
            : 'bg-white text-slate-800 hover:bg-slate-100 dark:bg-slate-800 dark:text-slate-100 dark:hover:bg-slate-700'}
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: 7,
            padding: '0.32rem 0.6rem',
            borderRadius: 8,
            border: active ? 'none' : `1px solid ${palette.border}`,
            fontSize: '0.74rem',
            fontWeight: 700,
            cursor: 'pointer',
          }}
        >
          <span>{currentLabel}</span>
          <span aria-hidden="true" style={{ fontSize: '0.6rem', opacity: 0.75 }}>▾</span>
        </button>
        {open && menuPos && (
          <div
            role="menu"
            className="rounded-xl border bg-white dark:bg-slate-800 dark:border-slate-600"
            style={{
              position: 'fixed',
              top: menuPos.top,
              right: menuPos.right,
              width: SCOPE_MENU_WIDTH,
              maxWidth: '78vw',
              maxHeight: `calc(100vh - ${menuPos.top + 12}px)`,
              overflowY: 'auto',
              zIndex: 60,
              padding: 6,
              boxShadow: '0 18px 40px -12px rgba(16,24,40,0.35), 0 6px 16px -8px rgba(16,24,40,0.2)',
            }}
          >
            {options.map((opt) => {
              const selected = isSelected(opt);
              return (
                <button
                  key={opt.key}
                  type="button"
                  role="menuitemradio"
                  aria-checked={selected}
                  onClick={() => select(opt.value)}
                  className={selected
                    ? 'w-full text-left rounded-lg text-teal-700 dark:text-teal-300 bg-teal-50 dark:bg-teal-900/30'
                    : 'w-full text-left rounded-lg text-slate-700 dark:text-slate-100 hover:bg-slate-100 dark:hover:bg-slate-700'}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    gap: 8,
                    padding: '0.5rem 0.6rem',
                  }}
                >
                  <span style={{ display: 'flex', flexDirection: 'column', minWidth: 0 }}>
                    <span style={{ fontSize: '0.8rem', fontWeight: selected ? 800 : 600 }}>{opt.label}</span>
                    {opt.sub && (
                      <span
                        className={selected ? 'text-teal-600 dark:text-teal-400' : 'text-slate-400 dark:text-slate-500'}
                        style={{
                          fontSize: '0.66rem',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                        }}
                      >
                        {opt.sub}
                      </span>
                    )}
                  </span>
                  {selected && <span aria-hidden="true" style={{ flexShrink: 0 }}>✓</span>}
                </button>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
